// Analytics tracking utilities for GA4 events
// SINGLE SOURCE OF TRUTH for all GA4 event dispatching
// Provides wrapper functions for consistent event tracking across the application

import { ALLOWED_ANALYTICS_COUNTRIES, isHostnameAllowed } from '@/constants/geo';
import { getCurrentIntent, type IntentTier } from '@/lib/luxuryIntentScoring';
import { signalToBoatData } from '@/lib/boatdataSignal';

declare global {
  interface Window {
    gtag?: (
      command: string,
      targetId: string,
      config?: Record<string, any>
    ) => void;
  }
}

// Check if geo is blocked (set by GeoAwareAnalytics or pre-check script)
const isGeoBlocked = (): boolean => {
  if (typeof window === 'undefined') return true;
  if ((window as any).__GA_BLOCKED === true) return true;
  try {
    const cache = localStorage.getItem('geo_check');
    if (cache) {
      const data = JSON.parse(cache);
      if (data.blocked) return true;
      if (data.country_code && !ALLOWED_ANALYTICS_COUNTRIES.includes(data.country_code)) {
        return true;
      }
    }
  } catch (e) {}
  return false;
};

// Check if gtag is available, hostname is allowed, AND geo is not blocked
const isGtagAvailable = (): boolean => {
  return typeof window !== 'undefined' && 
         'gtag' in window && 
         typeof window.gtag === 'function' &&
         isHostnameAllowed() &&
         !isGeoBlocked();
};

// ============================================================
// CORE EVENT DISPATCHER
// All events include source_site for multi-brand segmentation
// ============================================================

export const trackEvent = (eventName: string, parameters?: Record<string, any>) => {
  if (isGtagAvailable()) {
    const enrichedParams = {
      ...parameters,
      source_site: 'yachtvalues',
    };
    window.gtag?.('event', eventName, enrichedParams);
    if (process.env.NODE_ENV === 'development') {
      console.log('📊 GA4 Event:', eventName, enrichedParams);
    }
  }
};

// ============================================================
// LUXURY EVENT TAXONOMY (17 standardized events)
// Each includes: yacht_length, price_band, builder_name,
// engine_type, geo_region, engagement_score, intent_score,
// session_quality_score, source_channel
// ============================================================

interface LuxuryEventParams {
  yacht_length?: number;
  price_band?: string;
  builder_name?: string;
  engine_type?: string;
  geo_region?: string;
  engagement_score?: number;
  intent_score?: number;
  intent_tier?: IntentTier;
  session_quality_score?: number;
  source_channel?: string;
  [key: string]: any;
}

function enrichWithIntentScore(params: LuxuryEventParams = {}): LuxuryEventParams {
  try {
    const { score, tier } = getCurrentIntent();
    return {
      ...params,
      intent_score: params.intent_score ?? score,
      intent_tier: params.intent_tier ?? tier,
    };
  } catch {
    return params;
  }
}

// Valuation events (luxury threshold)
export const trackLuxuryValuationSubmit = (params: LuxuryEventParams & { valuation_mid: number }) => {
  const enriched = enrichWithIntentScore(params);
  trackEvent('luxury_valuation_submit', enriched);
  signalToBoatData({ event_type: 'luxury_valuation_submit', yacht_length: params.yacht_length, price_band: params.price_band, builder_name: params.builder_name, engine_type: params.engine_type, intent_score: enriched.intent_score, intent_tier: enriched.intent_tier as string, engagement_score: params.engagement_score, intent_type: 'seller', metadata: { valuation_mid: params.valuation_mid } });
};

export const trackSuperyachtValuationSubmit = (params: LuxuryEventParams & { valuation_mid: number }) => {
  const enriched = enrichWithIntentScore(params);
  trackEvent('superyacht_valuation_submit', enriched);
  signalToBoatData({ event_type: 'superyacht_valuation_submit', yacht_length: params.yacht_length, price_band: params.price_band, builder_name: params.builder_name, engine_type: params.engine_type, intent_score: enriched.intent_score, intent_tier: enriched.intent_tier as string, engagement_score: params.engagement_score, intent_type: 'seller', metadata: { valuation_mid: params.valuation_mid } });
};

// Builder & engine search
export const trackBuilderSearch = (builderName: string, params?: LuxuryEventParams) => {
  trackEvent('builder_search', enrichWithIntentScore({ ...params, builder_name: builderName }));
  signalToBoatData({ event_type: 'builder_search', builder_name: builderName, yacht_length: params?.yacht_length, price_band: params?.price_band, intent_type: 'buyer' });
};

export const trackEngineTypeSearch = (engineType: string, params?: LuxuryEventParams) => {
  trackEvent('engine_type_search', enrichWithIntentScore({ ...params, engine_type: engineType }));
};

// Conversion events
export const trackCashOfferClickLuxury = (params?: LuxuryEventParams) => {
  trackEvent('cash_offer_click', enrichWithIntentScore(params));
  signalToBoatData({ event_type: 'cash_offer_click', yacht_length: params?.yacht_length, price_band: params?.price_band, intent_type: 'seller' });
};

export const trackBrokerageInquiry = (params?: LuxuryEventParams) => {
  trackEvent('brokerage_inquiry', enrichWithIntentScore(params));
  signalToBoatData({ event_type: 'brokerage_inquiry', yacht_length: params?.yacht_length, price_band: params?.price_band, intent_type: 'seller' });
};

export const trackOffMarketRequest = (params?: LuxuryEventParams) => {
  trackEvent('off_market_request', enrichWithIntentScore(params));
  signalToBoatData({ event_type: 'off_market_request', yacht_length: params?.yacht_length, price_band: params?.price_band, intent_type: 'buyer' });
};

export const trackConciergeRequestLuxury = (params?: LuxuryEventParams) => {
  trackEvent('concierge_request', enrichWithIntentScore(params));
  signalToBoatData({ event_type: 'concierge_request', yacht_length: params?.yacht_length, price_band: params?.price_band, intent_type: 'seller' });
};

// Tool usage events
export const trackTradeUpUse = (params?: LuxuryEventParams) => {
  trackEvent('trade_up_use', enrichWithIntentScore(params));
  signalToBoatData({ event_type: 'trade_up_use', yacht_length: params?.yacht_length, price_band: params?.price_band, intent_type: 'buyer' });
};

export const trackNetProceedsUse = (params?: LuxuryEventParams) => {
  trackEvent('net_proceeds_use', enrichWithIntentScore(params));
  signalToBoatData({ event_type: 'net_proceeds_use', yacht_length: params?.yacht_length, price_band: params?.price_band, intent_type: 'seller' });
};

export const trackDocumentUpload = (params?: LuxuryEventParams) => {
  trackEvent('document_upload', enrichWithIntentScore(params));
  signalToBoatData({ event_type: 'document_upload', yacht_length: params?.yacht_length, intent_type: 'seller' });
};

export const trackSpecDownload = (params?: LuxuryEventParams) => {
  trackEvent('spec_download', enrichWithIntentScore(params));
  signalToBoatData({ event_type: 'spec_download', yacht_length: params?.yacht_length, builder_name: params?.builder_name, intent_type: 'buyer' });
};

export const trackVirtualTourView = (params?: LuxuryEventParams) => {
  trackEvent('virtual_tour_view', enrichWithIntentScore(params));
};

// Engagement events
export const trackRepeatVisitor7d = (params?: LuxuryEventParams) => {
  trackEvent('repeat_visitor_7d', enrichWithIntentScore(params));
};

export const trackRepeatVisitor30d = (params?: LuxuryEventParams) => {
  trackEvent('repeat_visitor_30d', enrichWithIntentScore(params));
};

export const trackHighValueSession = (params?: LuxuryEventParams & { time_on_site_seconds?: number; scroll_depth?: number }) => {
  trackEvent('high_value_session', enrichWithIntentScore(params));
  signalToBoatData({ event_type: 'high_value_session', engagement_score: params?.engagement_score, intent_type: 'research', metadata: { time_on_site_seconds: params?.time_on_site_seconds, scroll_depth: params?.scroll_depth } });
};

export const trackIntentEscalation = (params: LuxuryEventParams & { previous_tier: IntentTier; new_tier: IntentTier }) => {
  trackEvent('intent_escalation', enrichWithIntentScore(params));
  signalToBoatData({ event_type: 'intent_escalation', intent_score: params.intent_score, intent_tier: params.new_tier, intent_type: 'research', metadata: { previous_tier: params.previous_tier, new_tier: params.new_tier } });
};

// ============================================================
// EXISTING GA4 CONVENIENCE FUNCTIONS
// (Preserved for backward compatibility)
// ============================================================

export const trackButtonClick = (buttonName: string, location: string, destination?: string) => {
  trackEvent('button_click', { button_name: buttonName, location, destination });
};

export const trackCTAClick = (ctaName: string, destination: string, location: string) => {
  trackEvent('cta_click', { cta_name: ctaName, destination, location });
};

export const trackFormStart = (formName: string) => {
  trackEvent('form_start', { form_name: formName });
};

export const trackFormStep = (formName: string, step: number, stepName: string) => {
  trackEvent('form_step', { form_name: formName, step_number: step, step_name: stepName });
};

export const trackFormSubmit = (formName: string, success: boolean, errorMessage?: string) => {
  trackEvent('form_submit', { form_name: formName, success, error_message: errorMessage });
};

export const trackFormError = (formName: string, fieldName: string, errorType: string) => {
  trackEvent('form_error', { form_name: formName, field_name: fieldName, error_type: errorType });
};

export const trackGenerateLead = (leadType: string, source: string) => {
  trackEvent('generate_lead', { lead_type: leadType, source });
};

export const trackNavigation = (linkText: string, destination: string, location: string) => {
  trackEvent('navigation_click', { link_text: linkText, destination, location });
};

export const trackOutboundLink = (url: string, linkText: string, location: string) => {
  trackEvent('outbound_click', { url, link_text: linkText, location });
};

export const trackScrollDepth = (percentage: number, page: string) => {
  trackEvent('scroll', { percent_scrolled: percentage, page });
};

export const trackSearch = (searchTerm: string, resultsCount?: number) => {
  trackEvent('search', { search_term: searchTerm, results_count: resultsCount });
};

export const trackFileDownload = (fileName: string, fileType: string) => {
  trackEvent('file_download', { file_name: fileName, file_type: fileType });
};

export const trackVideoPlay = (videoTitle: string, videoUrl: string) => {
  trackEvent('video_play', { video_title: videoTitle, video_url: videoUrl });
};

export const trackEngagement = (action: string, category: string, label?: string) => {
  trackEvent('engagement', { action, category, label });
};

export const trackCaptchaComplete = (success: boolean) => {
  trackEvent('captcha_complete', { success });
};

export const trackPageView = (path: string, title?: string) => {
  if (isGtagAvailable()) {
    window.gtag?.('config', 'G-BD9WQCNXKH', { page_path: path, page_title: title });
  }
};

// Mobile-specific analytics
export const trackMobileInteraction = (action: string, details?: Record<string, any>) => {
  const isMobile = window.matchMedia('(max-width: 767px)').matches;
  const isTablet = window.matchMedia('(min-width: 768px) and (max-width: 1023px)').matches;
  trackEvent('mobile_interaction', {
    action,
    device_type: isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop',
    ...details,
  });
};

export const trackMobilePerformance = (metric: string, value: number) => {
  trackEvent('mobile_performance', {
    metric, value,
    connection_type: (navigator as any).connection?.effectiveType || 'unknown',
    device_memory: (navigator as any).deviceMemory || 'unknown',
  });
};

// Partner CTA tracking
export const trackPartnerCTAClick = (ctaType: string, pageType: string, placement: string) => {
  trackEvent('partner_cta_click', { cta_type: ctaType, page_type: pageType, placement });
};

export const trackPartnerCTAImpression = (ctaType: string, pageType: string, placement: string) => {
  trackEvent('partner_cta_impression', { cta_type: ctaType, page_type: pageType, placement });
};

// Buyer valuation tracking
export const trackBuyerValuationStart = (source: string, entryPoint: string, boatType?: string) => {
  trackEvent('buyer_valuation_start', { source, entry_point: entryPoint, boat_type: boatType });
};

export const trackBuyerValuationComplete = (boatType: string, lengthRange: string, newOrUsed: string) => {
  trackEvent('buyer_valuation_complete', { boat_type: boatType, length_range: lengthRange, new_or_used: newOrUsed });
};

export const trackSellerToBuyerConversion = (sellerValuationMid: number, boatType: string, context: string) => {
  trackEvent('seller_to_buyer_conversion', { seller_valuation_mid: sellerValuationMid, boat_type: boatType, context });
};

export const trackBuyerCTAClick = (location: string, variant: string, destination?: string) => {
  trackEvent('buyer_cta_click', { location, variant, destination: destination || '/buyer-valuation' });
};

// Phase 4 — Intent & Lead events
export const trackIntentCaptured = (
  intents: string[],
  context?: {
    valuationId?: string;
    brand?: string;
    model?: string;
    year?: number;
    length?: number;
    estimatedValue?: number;
  }
) => {
  trackEvent('intent_captured', enrichWithIntentScore({ intent_type: intents.join(','), valuation_id: context?.valuationId }));
  const hasSellerIntent = intents.some(i => ['considering_selling', 'need_broker'].includes(i));
  const hasBuyerIntent = intents.some(i => ['actively_shopping'].includes(i));
  signalToBoatData({
    event_type: 'intent_captured',
    valuation_id: context?.valuationId,
    intent_type: hasSellerIntent ? 'seller' : hasBuyerIntent ? 'buyer' : 'research',
    entity_context: context ? {
      brand: context.brand,
      model: context.model,
      year: context.year,
      length: context.length,
      estimated_value: context.estimatedValue,
    } : undefined,
    metadata: { intents },
  });
};

export const trackBrokerageLeadSubmit = (params?: LuxuryEventParams) => {
  trackEvent('brokerage_lead_submit', enrichWithIntentScore(params));
  signalToBoatData({ event_type: 'brokerage_lead_submit', intent_type: 'seller', yacht_length: params?.yacht_length, price_band: params?.price_band });
};

export const trackBuyerLeadSubmit = (params?: LuxuryEventParams) => {
  trackEvent('buyer_lead_submit', enrichWithIntentScore(params));
  signalToBoatData({ event_type: 'buyer_lead_submit', intent_type: 'buyer', yacht_length: params?.yacht_length, price_band: params?.price_band });
};

export const trackPartnerRouted = (partnerId: string, score: number) => {
  trackEvent('partner_routed', { partner_id: partnerId, lead_score: score });
};

// ============================================================
// MISSING SIGNAL TYPES — Added for BoaterData parity
// ============================================================

export const trackBuyerResearchStart = (brand?: string, boatType?: string) => {
  trackEvent('buyer_research_start', { brand, boat_type: boatType });
  signalToBoatData({ event_type: 'buyer_research_start', intent_type: 'buyer', entity_context: { brand }, metadata: { boat_type: boatType } });
};

export const trackFinancePrequalClick = (estimatedValue?: number, lengthRange?: string) => {
  trackEvent('finance_prequal_click', { estimated_value: estimatedValue, length_range: lengthRange });
  signalToBoatData({ event_type: 'finance_prequal_click', intent_type: 'finance', entity_context: { estimated_value: estimatedValue }, metadata: { length_range: lengthRange } });
};

export const trackInsuranceQuoteClick = (boatType?: string, estimatedValue?: number) => {
  trackEvent('insurance_quote_click', { boat_type: boatType, estimated_value: estimatedValue });
  signalToBoatData({ event_type: 'insurance_quote_click', intent_type: 'insurance', entity_context: { estimated_value: estimatedValue }, metadata: { boat_type: boatType } });
};

export const trackSignUp = () => {
  trackEvent('sign_up', { source_site: 'yachtvalues' });
  signalToBoatData({ event_type: 'sign_up', intent_type: 'research' });
};

export const trackBeginCheckout = (tier: string, amount?: number) => {
  trackEvent('begin_checkout', { tier, value: amount });
  signalToBoatData({ event_type: 'begin_checkout', intent_type: 'buyer', metadata: { tier, amount } });
};

// ============================================================
// LEAD AUTHENTICITY EVENTS
// ============================================================

export const trackLeadValidationFailed = (params: { reason: string; field: string }) => {
  trackEvent('yacht_lead_validation_failed', params);
};

export const trackDisposableEmailDetected = (params: { domain: string }) => {
  trackEvent('email_disposable_detected', params);
};

export const trackEmailDomainInvalid = (params: { domain: string }) => {
  trackEvent('email_domain_invalid', params);
};

export const trackLeadTrustScore = (params: {
  trust_score: number;
  trust_tier: string;
  email_domain: string;
  session_duration?: number;
  lead_value_estimate?: number;
}) => {
  trackEvent('yacht_lead_trust_score', params);
};
